import React, { createContext, useState, useEffect, useContext } from 'react';
import { useUnitDataCache } from './UnitDataCacheContext';
import { useThirdHeader } from './ThirdHeaderContext';
import { useSecondHeader } from './SecondHeaderContext';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

// Create the context
const UnitDataParserContext = createContext();

// Create a provider component
export const UnitDataParserProvider = ({ children }) => {
    const { t } = useTranslation();
    const { showUserEvaluations } = useThirdHeader();
    const { currentUnit } = useSecondHeader();
    const {
        activationsCounts,
        activityHeatmapData,
        categoryGroupedRecords,
        heatmapRecords,
        barchartRecords
    } = useUnitDataCache(); 

    const [categoryGrouped, setCategoryGrouped] = useState([]);

    const [activationsData, setActivationsData] = useState([]);
    const [donutData, setDonutData] = useState([]);
    const [heatmapData, setHeatmapData] = useState([]);
    const [barchartData, setBarchartData] = useState([]);

    const [barChartTimeUnit, setBarChartTimeUnit] = useState("year");
    const [barChartFrom, setBarChartFrom] = useState(0);

    const [votedCategoriesCategoryGrouped, setVotedCategoriesCategoryGrouped] = useState([]);
    const [votedCategoriesHeatmapData, setVotedCategoriesHeatmapData] = useState([]);
    const [votedCategoriesBarchartData, setVotedCategoriesBarchartData] = useState([]);

    const [categoryNames, setCategoryNames] = useState([]);
    const [colorForCategory, setColorForCategory] = useState({});
    
    const [localCustomEvalCategories, setLocalCustomEvalCategories] = useState([]);

    // Colors

    const [colors] = useState([
        '#74D5F2',
        '#BD92F4',
        '#81F3D5',
        '#FF8989',
        '#81ECF3',
        '#53C0FF',
        '#ED9BD2',
        '#FF89B0',
        '#90AAFF',
        '#81F3AD'
    ]);

    const special_categories = ["Kan ikke vurderes",]
    const specified_colors = {
        [special_categories[0]]: "#BFBFBF",
        "Ikke vurderet": "#D0D0D0",
        "UNDEFINED": "#A5A5A5"
    }
    const defaultColor = "#A5A5A5"

    const getColorForCategory = (category) => {
        return colorForCategory[category] ?? colorForCategory[t(category)] ?? defaultColor
    }

    const getVotedCategories = (records, completion, defaultToMachine = true) => {
        const votedCategoriesNewValue = records.map((record) => {
            if(record.user_evals) {
                const users_voted = Object.keys(record.user_evals)
                var votesForCategory = {}
                var highestVoted;
                var tie = false
                for (let i = 0; i < users_voted.length; i++) {
                    const category = record.user_evals[users_voted[i]]
                    
                    if (category) {
                        if (votesForCategory[category]) {
                            votesForCategory[category] += 1
                        } else {
                            votesForCategory[category] = 1
                        }

                        if(!highestVoted) {
                            highestVoted = category
                        } else if (highestVoted != category) {
                            const votes = votesForCategory[category]

                            if (votes > 0) {
                                const highestVotes = votesForCategory[highestVoted]

                                if (votes > highestVotes) {
                                    highestVoted = category
                                    tie = false
                                } else if (votes === highestVotes) {
                                    tie = true
                                }
                            }
                        }
                    }
                }

                if (tie) {
                    return "Uafgjort"
                }
                
                if (highestVoted) {
                    return highestVoted
                }
            }
    
            return defaultToMachine ? record.eval : undefined
        })

        completion(votedCategoriesNewValue)
    }

    useEffect(()=>{
        if(categoryGroupedRecords) {
            const defaultToMachine = true
            getVotedCategories(categoryGroupedRecords, setVotedCategoriesCategoryGrouped, defaultToMachine)
        }
    }, [categoryGroupedRecords])

    useEffect(()=>{
        if (heatmapRecords) {
            getVotedCategories(heatmapRecords, setVotedCategoriesHeatmapData)
        }
    }, [heatmapRecords])

    useEffect(()=>{
        if (barchartRecords) {
            getVotedCategories(barchartRecords, setVotedCategoriesBarchartData)
        }
    }, [barchartRecords])

    const updateCategoryGroupedWithUserEvaluations = (includeNonEvaluated = true) => {
        var activationsDataNewValue = []

        if (currentUnit) {
                
            // Add standard categories
            if (currentUnit.deployment_config?.classification?.class_names) {
                for (var i = 0; i < currentUnit.deployment_config.classification.class_names.length; i++) {
                    const category = currentUnit.deployment_config.classification.class_names[i]
                    activationsDataNewValue.push({ category: category, count: 0 })
                }
            }

            // Add user added categories

            if (currentUnit.custom_categories) {
                for (let i = 0; i < currentUnit.custom_categories.length; i++) {
                    const category = currentUnit.custom_categories[i]
                    activationsDataNewValue.push({ category: category, count: 0 })
                }
            }
        }

        for (let i = 0; i < categoryGroupedRecords.length; i++) {
            let category = votedCategoriesCategoryGrouped[i]
            if (!category) {
                if (includeNonEvaluated) {
                    category = "Ikke vurderet"
                } else {
                    continue
                }
            }
            const categoryIndex = activationsDataNewValue.findIndex(
                (element) => element.category === category
            )

            if (categoryIndex === -1) {
                activationsDataNewValue.push({ category: category, count: 1 })
            } else {
                activationsDataNewValue[categoryIndex].count += 1
            }
        }

        // Put category with name "UNDEFINED" at the end of the default categories
        const defaultCategoriesCount = currentUnit?.deployment_config?.classification?.class_names?.length ?? 0
        const undefinedTargetIndex = defaultCategoriesCount - 1
        const undefinedSourceIndex = activationsDataNewValue.findIndex((element) => element.category === "UNDEFINED")
        if (undefinedSourceIndex !== -1) {
            const undefinedCategory = activationsDataNewValue.splice(undefinedSourceIndex, 1)[0]
            activationsDataNewValue.splice(undefinedTargetIndex, 0, undefinedCategory)
        }

        setCategoryGrouped(activationsDataNewValue)
    }

    const updateHeatmapDataWithUserEvaluations = () => {
        var heatmapDataNewValue = []

        if (currentUnit) {
                
            // Add standard categories
            if (currentUnit.deployment_config?.classification?.class_names) {
                for (let i = 0; i < currentUnit.deployment_config.classification.class_names.length; i++) {
                    const category = currentUnit.deployment_config.classification.class_names[i]
                    let counts = []
                    for (let j = 0; j < 7; j++) {
                        counts.push(0)
                    }
                    heatmapDataNewValue.push({ category: category, counts: counts })
                }
            }

            // Add user added categories

            if (currentUnit.custom_categories) {
                for (let i = 0; i < currentUnit.custom_categories.length; i++) {
                    const category = currentUnit.custom_categories[i]
                    let counts = []
                    for (let j = 0; j < 7; j++) {
                        counts.push(0)
                    }
                    heatmapDataNewValue.push({ category: category, counts: counts })
                }
            }
        }

        // Add counts

        for (let i = 0; i < heatmapRecords.length; i++) {
            const category = votedCategoriesHeatmapData[i] ?? "Ikke vurderet"
            const categoryIndex = heatmapDataNewValue.findIndex((element) => element.category === category)

            // Find week day of record
            const timestamp = heatmapRecords[i].timestamp
            const day = new Date(timestamp).getDay()
            const mondayIndexed = day === 0 ? 6 : day - 1

            let counts = []
            for (let j = 0; j < 7; j++) {
                if (j === mondayIndexed) {
                    counts.push(1)
                } else {
                    counts.push(0)
                }
            }

            if (categoryIndex === -1) {
                heatmapDataNewValue.push({ category: category, counts: counts })
            } else {
                heatmapDataNewValue[categoryIndex].counts[mondayIndexed] += 1
            }
        }

        setHeatmapData(heatmapDataNewValue)
    }

    function getDaysInMonth(date) {
        const month = date.getMonth();
        const year = date.getFullYear();
        return new Date(year, month + 1, 0).getDate();
    }

    const updateActivityOverTimeDataWithUserEvaluations = () => {
        var activityOverTimeDataNewValue = []

        const firstDate = new Date(barChartFrom)
        var timeUnitMaxValue = 0
        switch (barChartTimeUnit) {
            case "year":
                timeUnitMaxValue = 12
                break;
            case "month":
                timeUnitMaxValue = getDaysInMonth(firstDate)
                break;
            case "week":
                timeUnitMaxValue = 7
                break;
            default:
                break;
        }

        if (currentUnit) {
            // Add standard categories
            if (currentUnit.deployment_config?.classification?.class_names) {
                for (let i = 0; i < currentUnit.deployment_config.classification.class_names.length; i++) {
                    const category = currentUnit.deployment_config.classification.class_names[i]
                    var counts = []
                    for (var j = 0; j < timeUnitMaxValue; j++) {
                        counts.push(0)
                    }
                    activityOverTimeDataNewValue.push({ category: category, counts: counts })
                }
            }


            // Add user added categories

            if (currentUnit.custom_categories) {
                for (let i = 0; i < currentUnit.custom_categories.length; i++) {
                    const category = currentUnit.custom_categories[i]
                    var counts = []
                    for (let j = 0; j < timeUnitMaxValue; j++) {
                        counts.push(0)
                    }
                    activityOverTimeDataNewValue.push({ category: category, counts: counts })
                }
            }

        }
        
        for (let i = 0; i < barchartRecords.length; i++) {
            const category = votedCategoriesBarchartData[i] ?? "Ikke vurderet"
            const categoryIndex = activityOverTimeDataNewValue.findIndex((element) => element.category === category)
            
            const timestamp = barchartRecords[i].timestamp
            var timeWithinUnit = 0
            const date = new Date(timestamp)
            switch (barChartTimeUnit) {
                case "year":
                    timeWithinUnit = date.getMonth()
                    break;
                case "month":
                    timeWithinUnit = date.getDate() - 1
                    break;
                case "week":
                    const sundayIndexed = date.getDay()
                    timeWithinUnit = sundayIndexed === 0 ? 6 : sundayIndexed - 1
                    break;
                default:
                    break;
            }

            if (categoryIndex === -1) {
                let _counts = []
                for (let k = 0; k < timeUnitMaxValue; k++) {
                    if (k === timeWithinUnit) {
                        _counts.push(1)
                    } else {
                        _counts.push(0)
                    }
                }
                activityOverTimeDataNewValue.push({ category: category, counts: _counts })
            } else {
                activityOverTimeDataNewValue[categoryIndex].counts[timeWithinUnit] += 1
            }
        }

        setBarchartData(activityOverTimeDataNewValue)
    }

    const updateActivityOverTimeDataWithoutUserEvaluations = () => {
        var activityOverTimeDataNewValue = []

        const firstDate = new Date(barChartFrom)
        var timeUnitMaxValue = 0
        switch (barChartTimeUnit) {
            case "year":
                timeUnitMaxValue = 12
                break;
            case "month":
                timeUnitMaxValue = getDaysInMonth(firstDate)
                break;
            case "week":
                timeUnitMaxValue = 7
                break;
            default:
                break;
        }

        if (currentUnit) {
                
            // Add standard categories
            if (currentUnit.deployment_config?.classification?.class_names) {
                for (let i = 0; i < currentUnit.deployment_config.classification.class_names.length; i++) {
                    const category = currentUnit.deployment_config.classification.class_names[i]
                    var counts = []
                    for (var j = 0; j < timeUnitMaxValue; j++) {
                        counts.push(0)
                    }
                    activityOverTimeDataNewValue.push({ category: category, counts: counts })
                }
            }

            // Add user added categories

            if (currentUnit.custom_categories) {
                for (let i = 0; i < currentUnit.custom_categories.length; i++) {
                    const category = currentUnit.custom_categories[i]
                    var counts = []
                    for (let j = 0; j < timeUnitMaxValue; j++) {
                        counts.push(0)
                    }
                    activityOverTimeDataNewValue.push({ category: category, counts: counts })
                }
            }
        }
        
        for (let i = 0; i < barchartRecords.length; i++) {
            const category = barchartRecords[i].eval
            const categoryIndex = activityOverTimeDataNewValue.findIndex((element) => element.category === category)

            const timestamp = barchartRecords[i].timestamp
            var timeWithinUnit = 0
            const date = new Date(timestamp)
            switch (barChartTimeUnit) {
                case "year":
                    timeWithinUnit = date.getMonth()
                    break;
                case "month":
                    timeWithinUnit = date.getDate() - 1
                    break;
                case "week":
                    const sundayIndexed = date.getDay()
                    timeWithinUnit = sundayIndexed === 0 ? 6 : sundayIndexed - 1
                    break;
                default:
                    break;
            }

            if (categoryIndex === -1) {
                let _counts = []
                for (let k = 0; k < timeUnitMaxValue; k++) {
                    if (k === timeWithinUnit) {
                        _counts.push(1)
                    } else {
                        _counts.push(0)
                    }
                }
                activityOverTimeDataNewValue.push({ category: category, counts: _counts })
            } else {
                activityOverTimeDataNewValue[categoryIndex].counts[timeWithinUnit] += 1
            }
        }

        setBarchartData(activityOverTimeDataNewValue)
    }

    const isCustomCategoryAlreadyIncluded = (category) => {
        var output = false;
        if (currentUnit && currentUnit.deployment_config?.classification?.class_names) {
            for (let i = 0; i < currentUnit.deployment_config.classification.class_names.length && !output; i++) {
                const translatedEval = t(currentUnit.deployment_config.classification.class_names[i]);
                if (translatedEval === category) {
                    output = true;
                }
            }
        }

        return output;
    }

    useEffect(() => {
        if (showUserEvaluations) {
            const includeNonEvaluated = false
            updateCategoryGroupedWithUserEvaluations(includeNonEvaluated)
        }
    }, [votedCategoriesCategoryGrouped, showUserEvaluations])

    useEffect(() => {
        if (showUserEvaluations) {
            updateHeatmapDataWithUserEvaluations()
        }
    }, [votedCategoriesHeatmapData, showUserEvaluations])

    useEffect(() => {
        if (showUserEvaluations) {
            updateActivityOverTimeDataWithUserEvaluations()
        } else {
            updateActivityOverTimeDataWithoutUserEvaluations()
        }
    }, [votedCategoriesBarchartData, showUserEvaluations, barChartTimeUnit])

    useEffect(() => {
        if (!showUserEvaluations && activationsCounts) {
            setCategoryGrouped(activationsCounts);
        }
    }, [activationsCounts, showUserEvaluations])

    useEffect(() => {
        if (!showUserEvaluations && activityHeatmapData) {

            setHeatmapData(activityHeatmapData);
        }
    }, [activityHeatmapData, showUserEvaluations])

    useEffect(() => {
        if (categoryGrouped) {
            setActivationsData(categoryGrouped);
            setDonutData(categoryGrouped);
        }
    }, [categoryGrouped])

    useEffect(() => {
        if (categoryNames && colors) {
            const colorForCategoryNewValue = {}
            for (var i = 0; i < categoryNames.length; i++) {
                const category = categoryNames[i]
                const translatedCategory = t(category)
                if (!specified_colors[translatedCategory]) {
                    colorForCategoryNewValue[translatedCategory] = colors[i % colors.length]
                }
            }

            for(let key in specified_colors) {
                colorForCategoryNewValue[key] = specified_colors[key]
            }

            setColorForCategory(colorForCategoryNewValue)
        }
    }, [colors, categoryNames])

    useEffect(() => {
        if (currentUnit) {
            
            var localCustomEvalCategoriesNewValue = []
            
            // Add special caregories
            for (var i = 0; i < special_categories.length; i++) {
                localCustomEvalCategoriesNewValue.push(t(special_categories[i]))
            }

            // Add custom categories
            if (currentUnit.custom_categories) {
                for (var j = 0; j <  currentUnit.custom_categories.length; j++) {
                    let category = currentUnit.custom_categories[j]
                    if (!isCustomCategoryAlreadyIncluded(category)) {
                        localCustomEvalCategoriesNewValue.push(currentUnit.custom_categories[j])
                    }
                }
            }

            if (currentUnit.aris_custom_categories) {
                for (var j = 0; j <  currentUnit.aris_custom_categories.length; j++) {
                    let category = currentUnit.aris_custom_categories[j]
                    if (!isCustomCategoryAlreadyIncluded(category)) {
                        localCustomEvalCategoriesNewValue.push(currentUnit.aris_custom_categories[j])
                    }
                }
            }

            // Get vote counts for custom categories
            // For display_units, we need to get unit_id from unit_ranges, or skip if no valid unit_id
            const unitId = currentUnit.unit_id || (currentUnit.unit_ranges?.[0]?.unit_id);
            const votesForCategory = {}
            
            // Only query if we have a valid unitId and categories to query
            if (unitId && localCustomEvalCategoriesNewValue && localCustomEvalCategoriesNewValue.length > 0) {
                // Filter out any undefined/null values from the array
                const validCategories = localCustomEvalCategoriesNewValue.filter(cat => cat !== undefined && cat !== null);
                
                if (validCategories.length > 0) {
                    const recordCategoryCollection = collection(db, "record_categories")
                    const _query = query(
                        recordCategoryCollection, 
                        where("unit", "==", unitId), 
                        where("name", "in", validCategories)
                    )
                    getDocs(_query).then(snapshot => {
                        for (let i = 0; i < snapshot.docs.length; i++) {
                            const _doc = snapshot.docs[i]
                            const snapshotDict = _doc.data() || {}
                            const votes = snapshotDict["votes"]
                            const name = snapshotDict["name"]
                            if (name) {
                                votesForCategory[name] = votes
                            }
                        }
                        
                        // Sort after getting votes
                        localCustomEvalCategoriesNewValue.sort((a, b) => {
                            return (votesForCategory[b] || 0) - (votesForCategory[a] || 0)
                        })
                        setLocalCustomEvalCategories(localCustomEvalCategoriesNewValue);
                    }).catch(error => {
                        console.error("Error fetching record categories:", error);
                        // Still set categories even if query fails
                        setLocalCustomEvalCategories(localCustomEvalCategoriesNewValue);
                    })
                    return; // Exit early since we're handling the setState in the promise
                }
            }

            // Sort (if no query was made or if categories array is empty)
            localCustomEvalCategoriesNewValue.sort((a, b) => {
                return (votesForCategory[b] || 0) - (votesForCategory[a] || 0)
            })

            setLocalCustomEvalCategories(localCustomEvalCategoriesNewValue);
        }
    }, [currentUnit]);

    useEffect(() => {
        if (currentUnit) {
            var _categoryNames = (currentUnit.deployment_config?.classification?.class_names ?? []).concat(localCustomEvalCategories)
            const additionalFractions = (currentUnit.deployment_config?.classification?.fractions ?? []).filter(fraction => {
                return !_categoryNames.includes(fraction)
            })

            _categoryNames = _categoryNames.concat(additionalFractions)

            setCategoryNames(_categoryNames)
        }
    }, [currentUnit, localCustomEvalCategories]);

    return (
        <UnitDataParserContext.Provider value={{
            activationsData,
            donutData,
            heatmapData,
            barchartData,

            getColorForCategory,
            localCustomEvalCategories,

            setBarChartTimeUnit,
            setBarChartFrom,
            setLocalCustomEvalCategories
            
        }}>
            {children}
        </UnitDataParserContext.Provider>
    );
};

export const useUnitDataParser = () => {
    return useContext(UnitDataParserContext);
};
